import { artifactList } from '../transport/api'
import { ApiError } from '../transport/http'
import { fetchServers, type PairedServer } from '../servers/serverList'
import type { FrameShell } from '../shell/shellOrigin'
import type { SharedArtifact } from '../transport/relay'

/**
 * EVERY ARTIFACT THIS ACCOUNT PUBLISHES, AND THE MACHINE EACH ONE LIVES ON.
 *
 * Owner, 2026-09-20: 「Artifact 上面也要顯示它是從哪一台機器過來的。而且機器是否
 * 是離線,然後如果機器是離線的話,就不應該讓使用者更改它的狀態」.
 *
 * An artifact is PER-MACHINE state — `~/.avengents/artifacts.json` on the box
 * that publishes it — so there is no account-wide list anywhere to read. The
 * rail's list is a fan-out, exactly like the agent roster's: ask each paired
 * machine what it publishes, and keep WHICH machine answered attached to every
 * row, because the machine is half of what the person needs to know.
 *
 * AN OFFLINE MACHINE IS A FACT, NOT A GAP. It is listed with what it cannot
 * tell us, rather than omitted — a machine that is merely off must not make its
 * artifacts look deleted, and the artifacts it published are still claimed at
 * the relay. The same rule the agent roster learned: "this machine has none"
 * and "we could not ask" are different sentences.
 */
export type ArtifactVisibility = 'private' | 'friends' | 'public'

/** One row as the edge's /artifact/list gives it, plus where it came from. */
export type ArtifactRow = {
  hash: string
  kind: 'static' | 'port'
  summary: string
  name: string
  visibility: ArtifactVisibility
  /** The public URL, or '' when the relay has not confirmed one. */
  url: string
  /** The folder, or http://127.0.0.1:<port> — what it serves FROM. */
  source: string
  /** The relay is holding this hash right now. */
  live: boolean
  /** Why the relay refused it, when it did. */
  refused?: string
  /**
   * WHICH friends may open it — owner, 2026-09-20. Absent means every friend,
   * which is what "friends" meant before the picker and therefore what every
   * artifact published before it means. An empty array means nobody, which the
   * picker's "Uncheck all" produces and is NOT the same sentence as absent.
   */
  allow?: string[]
  /**
   * Published NARROWER than it is set, and why.
   *
   * Today one cause: a chosen-friends list on a relay too old to enforce one,
   * which the machine publishes private rather than as plain "friends" —
   * because that older build would drop the list and serve the page to the
   * whole friend list. The artifact is live, just not as its owner set it, and
   * that difference has to reach them.
   */
  held?: string
  /** WHICH MACHINE — the owner asked for this explicitly. */
  device_id: string
  machine: string
}

/** What the relay link on one machine is doing, as that machine reports it. */
export type ArtifactLink = {
  connected: boolean
  base: string
  detail: string
  accepted?: boolean
  paused?: string
  terms_url?: string
}

export type ArtifactMachine = {
  device_id: string
  name: string
  /** From the account's own server list, not from the machine's answer. */
  reachable: PairedServer['reachable']
  artifacts: ArtifactRow[]
  link: ArtifactLink | null
  /** In the machine's own words when it could not answer. */
  error: string | null
}

export type ArtifactsEverywhere = {
  rows: ArtifactRow[]
  machines: ArtifactMachine[]
  /** Set when the account's machine list itself could not be read — which is a
   *  different thing from "this account publishes nothing". */
  listError: string | null
  /**
   * HOW MANY MACHINES THIS ANSWER WILL EVENTUALLY COVER.
   *
   * The fan-out publishes each machine's answer as it arrives, so `machines` is
   * a PARTIAL list for as long as the slowest box is still being asked. Without
   * this number a reader cannot tell "asked everyone, found nothing" from "asked
   * one of six so far" — and the first machine to answer is usually one with no
   * artifacts, so the panel stated "Nothing published yet" over an account that
   * publishes one. Measured on dev: the true row appeared roughly ten seconds
   * after that sentence.
   *
   * `machines.length >= expected` is the only honest test for "settled".
   */
  expected: number
}

type ListAnswer = {
  ok?: boolean
  items?: Array<Omit<ArtifactRow, 'device_id' | 'machine'>>
  link?: ArtifactLink
}

/**
 * "This edge has no artifact route" — the one refusal that is not a fault.
 *
 * Read from the STATUS and the error CODE, never from the message text: the
 * message IS the code (`no_such_route` / `not_found`), so a substring test for
 * "not found" misses both.
 */
function routeMissing(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false
  if (e.status === 404) return true
  const code = (e.body as { error?: string } | null)?.error
  return code === 'no_such_route' || code === 'not_found'
}

/** Same shape and same reason as the agent roster's: never all at once. */
async function mapLimited<T>(
  servers: PairedServer[],
  ask: (s: PairedServer) => Promise<T>,
  onResult?: (value: T) => void,
): Promise<T[]> {
  const LIMIT = 3
  const out = new Array<T>(servers.length)
  let next = 0
  const worker = async (): Promise<void> => {
    for (;;) {
      const i = next++
      if (i >= servers.length) return
      out[i] = await ask(servers[i]!)
      onResult?.(out[i]!)
    }
  }
  await Promise.all(Array.from({ length: Math.min(LIMIT, servers.length) }, worker))
  return out
}

export function mergeArtifacts(
  prev: ArtifactsEverywhere | undefined,
  m: ArtifactMachine,
  /** How many machines are being asked in total; kept from `prev` when omitted. */
  expected?: number,
): ArtifactsEverywhere {
  const machines = (prev?.machines ?? []).filter((x) => x.device_id !== m.device_id)
  machines.push(m)
  machines.sort((a, b) => a.name.localeCompare(b.name))
  return {
    rows: machines.flatMap((x) => x.artifacts),
    machines,
    listError: prev?.listError ?? null,
    /*
     * Never below what we already hold: a merge that lowered it would make a
     * partial answer look settled, which is the failure this field exists for.
     */
    expected: Math.max(expected ?? prev?.expected ?? 0, machines.length),
  }
}

/** True once every machine in the account has answered — see `expected`. */
export function artifactsSettled(a: ArtifactsEverywhere | undefined): boolean {
  return !!a && a.machines.length >= a.expected
}

export async function getArtifactsEverywhere(opts?: {
  onMachine?: (m: ArtifactMachine) => void
  /** Fired once the account's machine list is known, before any of them answer. */
  onExpected?: (n: number) => void
}): Promise<ArtifactsEverywhere> {
  const list = await fetchServers()
  if (list.kind !== 'servers') {
    // "No machines paired" and "we could not ask" are different sentences, and
    // neither of them is "this account publishes nothing".
    return {
      rows: [],
      machines: [],
      listError: list.kind === 'none' ? null : list.kind === 'unreachable' ? list.detail : 'no device list',
      expected: 0,
    }
  }
  opts?.onExpected?.(list.servers.length)
  const machines = await mapLimited<ArtifactMachine>(
    list.servers,
    async (s): Promise<ArtifactMachine> => {
      const base = {
        device_id: s.server_id,
        name: s.name,
        reachable: s.reachable,
        artifacts: [] as ArtifactRow[],
        link: null as ArtifactLink | null,
      }
      /*
       * A machine the account already knows is offline is not asked at all.
       * The request would hang until the relay gave up, and the answer is
       * already known — so say it, and say it fast.
       */
      if (s.reachable === 'offline') {
        return { ...base, error: 'this machine is offline' }
      }
      try {
        const r: ListAnswer = await artifactList(s.server_id)
        const items = Array.isArray(r.items) ? r.items : []
        return {
          ...base,
          artifacts: items.map((i) => ({ ...i, device_id: s.server_id, machine: s.name })),
          link: r.link ?? null,
          error: null,
        }
      } catch (e) {
        /*
         * An edge too old to have artifacts answers 404, which is not a fault
         * and must not read as one — the same rule the room-push toggle
         * follows when its own route is missing.
         *
         * ⚠️ BY CODE, NOT BY TEXT. This tested `/404|not found/i` against the
         * error MESSAGE, and the message is the error CODE verbatim
         * (http.ts:220) — `no_such_route` from the relay, `not_found` from the
         * edge. Neither contains "404" or "not found" (note the underscore), so
         * every machine on an older build was reported as "Could not ask <name>"
         * — the precise false alarm the paragraph above says must not happen.
         * Found by looking at a screenshot of the empty state, 2026-09-20; no
         * assertion covered it, because the fixture threw a plain Error whose
         * message happened to match.
         */
        const detail = e instanceof Error ? e.message : String(e)
        return { ...base, error: routeMissing(e) ? '' : detail }
      }
    },
    (m) => opts?.onMachine?.(m),
  )
  const kept = machines.sort((a, b) => a.name.localeCompare(b.name))
  return {
    rows: kept.flatMap((m) => m.artifacts),
    machines: kept,
    listError: null,
    expected: kept.length,
  }
}

/** True when this machine cannot be asked to change anything right now. */
export function machineIsReachable(m: Pick<ArtifactMachine, 'reachable' | 'error'>): boolean {
  return m.reachable !== 'offline' && !m.error
}

/**
 * MAY THE IN-APP VIEWER PUT THIS ARTIFACT IN A FRAME?
 *
 * The app runs AT the backend's own origin — `capacitor.config.ts` sets
 * `server.url` to it, and the web app is served from it — so an artifact at
 * `<origin>/artifacts/<hash>/` is SAME-ORIGIN with the app. That is what makes
 * the in-app viewer work without a second sign-in (owner, 2026-09-20: 「因為是
 * In-App Browser,所以直接 identify 使用者的身分」): the session cookie is already
 * in the jar, so the relay's own auth hop happens inside the frame and nobody is
 * asked anything.
 *
 * ⚠️ IT IS ALSO THE WHOLE RISK, so the rule is written here rather than left to
 * whoever adds the next door. Same-origin means the framed page can reach
 * `window.parent` — the app's DOM, its in-flight state, everything. For an
 * artifact published by ONE OF THIS ACCOUNT'S OWN MACHINES that is not an
 * escalation: the machine serving it already holds that account's edge
 * credentials, so code able to run there can already act as the account without
 * any of this. For an artifact published by SOMEONE ELSE it is a full takeover of
 * the viewer's session, and no attribute on the frame prevents it — `sandbox`
 * without `allow-same-origin` would, and it also breaks cookies, storage and
 * same-origin fetch in the very apps artifacts exist to publish
 * (docs/ARTIFACTS.md §2, the accepted residual).
 *
 * So the gate is ownership, and `machine` carries it: it is resolved from the
 * account's OWN machine list, so a non-null machine means "this account publishes
 * this". An artifact that came from anywhere else MUST NOT be let through here.
 * It is the one thing about this surface that cannot be fixed afterwards.
 *
 * WHAT THE ARTIFACTS' OWN HOST CHANGED, AND WHAT IT DID NOT (owner, 2026-10-05).
 * This paragraph used to end by saying that friends' artifacts must wait "until
 * those have an origin of their own". They have one now, where the cloud is set
 * up for it, and `sharedViewGate` below is their door. THIS gate is unchanged
 * and is still the only one under which a page is framed at the App's own
 * origin: where artifacts are still served here, and whenever the other host
 * cannot be used, an own artifact is framed exactly as before — and nobody
 * else's is framed at all.
 *
 * The same ruling also REJECTS the reasoning above for one's own artifacts ("not an
 * escalation"): 就算這個頁面, 我是頁面的使用者, 建造者, 我也沒有辦法直接從頁面來控制
 * 我的 agent. So a same-origin own frame is what is LEFT of the old shape, not a
 * design to keep: it ends, per environment, the day that environment's artifacts
 * get their host, and nothing new may be built on it.
 */
export function inAppViewGate(
  row: Pick<ArtifactRow, 'url' | 'machine'>,
  machine: ArtifactMachine | null,
): { ok: boolean; why: string } {
  if (!machine) {
    return {
      ok: false,
      why: `This app cannot see ${row.machine} among this account’s machines, so it will not open what that address serves.`,
    }
  }
  if (machine.reachable === 'offline') {
    return { ok: false, why: `${row.machine} is offline, so there is nothing to show.` }
  }
  if (!row.url) {
    return { ok: false, why: 'The relay has not confirmed an address for this artifact yet.' }
  }
  return { ok: true, why: '' }
}

/**
 * WHO SHARES IT, as this account knows them — their name, else their address,
 * and never a blank. One spelling, because the row in More and the viewer's
 * header say it about the same artifact a tap apart.
 */
export function sharerOf(a: Pick<SharedArtifact, 'shared_by'>): string {
  return a.shared_by.name || a.shared_by.email || 'a friend'
}

/** A verdict on one frame: the exact address to load, or why there is none. */
export type FrameVerdict = { ok: true; src: string } | { ok: false; why: string }

/** An absolute http(s) address, or null — never a relative one, never another scheme. */
function webUrl(s: string | null | undefined): URL | null {
  if (!s) return null
  try {
    const u = new URL(s)
    return u.protocol === 'https:' || u.protocol === 'http:' ? u : null
  } catch {
    return null
  }
}

/**
 * THE ARTIFACTS' OWN HOST, as far as this page may use it — its origin, else null.
 *
 * Owner, 2026-10-05: artifact pages move to a hostname of their own, so that the
 * browser itself keeps every artifact page — a friend's or one's own — apart from
 * the App. `contentBase` is what the cloud answers when asked where artifacts are
 * served (`<content host>/artifacts`), or null when that is not known: an older
 * cloud, a request that failed, a cloud with no such host.
 *
 * NULL IS THE ORDINARY ANSWER, and every caller treats it as "nothing has changed".
 * It is also the answer for two addresses that would look like a host and are not
 * one: the App's OWN origin (a page there is not apart from the App at all, which
 * is the whole point of the move), and plain http under an https App (the browser
 * refuses such a frame, and a blank pane is worse than the fallback).
 */
export function artifactsHostOf(contentBase: string | null, appOrigin: string): string | null {
  const base = webUrl(contentBase)
  if (!base) return null
  if (base.protocol !== 'https:' && !appOrigin.startsWith('http:')) return null
  return base.origin === appOrigin ? null : base.origin
}

/**
 * WHAT MAY BE FRAMED FROM THE ARTIFACTS' OWN HOST — owner, 2026-10-05.
 *
 * A frame on another origin cannot reach into `window.parent`, cannot read the
 * App's storage and cannot read what the App's cloud answers it. That is the
 * "origin of their own" the gate above was waiting for, so on that host the rule
 * is no longer WHO published the page. It is the ADDRESS, and all of it is here:
 *
 *  1. Artifacts have a host of their own that this page may use (`artifactsHostOf`).
 *  2. The address to frame is ON that host — same origin as the content base, not
 *     merely "some other origin". When this page runs on a developer origin, the
 *     App host of a real environment is also another origin, and a person may be
 *     signed in there.
 *  3. Which makes it NOT this page's origin. That is the sentence the change exists
 *     for: a page that is not this account's is never framed at the App's own origin.
 *  4. It is this artifact's own sign-in door and nothing else on that host:
 *     `/artifacts/<the same hash>/_avg/auth`, carrying a ticket (`vt`). The relay
 *     trades the ticket for the artifact; this app hands a frame no other path.
 *
 * `src` is the address as it was CHECKED — the parsed form, so what is framed is
 * what was judged.
 *
 * WHAT THE OTHER HOST DOES NOT SEPARATE: artifacts from each other (they all share
 * that one origin), and a page from a native shell's bridges — `sharedViewGate`.
 */
export function contentFrameGate(a: {
  /** The ticket address the cloud gave: a shared row's `frame_url`, or the ticket route's. */
  frameUrl: string | null | undefined
  hash: string
  /** `<content host>/artifacts` as the cloud states it; null when it is not known. */
  contentBase: string | null
  /** This page's own origin — `window.location.origin` at the call site. */
  appOrigin: string
}): FrameVerdict {
  const host = artifactsHostOf(a.contentBase, a.appOrigin)
  if (!host) {
    return {
      ok: false,
      why: 'This app has not been given a separate address for artifacts, so it will not show this one inside the app.',
    }
  }
  const u = webUrl(a.frameUrl)
  if (!u) return { ok: false, why: 'The cloud gave no address this app can frame for this artifact.' }
  if (u.origin !== host) {
    return { ok: false, why: 'The address given for this artifact is not on the artifacts’ own host.' }
  }
  if (!/^[a-f0-9]{24}$/.test(a.hash) || u.pathname !== `/artifacts/${a.hash}/_avg/auth`) {
    return { ok: false, why: 'The address given does not lead to this artifact.' }
  }
  if (!u.searchParams.get('vt')) {
    return { ok: false, why: 'The address given for this artifact carries no ticket.' }
  }
  return { ok: true, src: u.href }
}

/**
 * MAY THE IN-APP VIEWER FRAME AN ARTIFACT THAT IS NOT THIS ACCOUNT'S?
 *
 * Owner, 2026-10-05: a friend's shared artifact opens in the App's main pane, with
 * the built-in viewer rather than a separate window.
 *
 * Everything `contentFrameGate` demands, and one thing more — because another
 * origin is a wall only where the browser is the only thing in the room. A native
 * shell gives its web view BRIDGES, and WKWebView offers them to every frame: a
 * framed page cannot read the App, but it can post to the handler that runs the
 * shell's plugins, and Capacitor's handler does not ask which frame is calling.
 * So where there is a shell, the shell has to have said it checks:
 *
 *   no shell     a plain browser — there is no bridge to reach.
 *   Mac          its own bridges already answer only the main frame at the App's
 *                origin.
 *   Capacitor    only a build that says so with `AvengentsFrameGuard/<n>` in its
 *                User-Agent (shell/shellOrigin.ts `frameShell`; the shell's half of
 *                the contract is docs/NATIVE.md §16). Every build before that token
 *                is refused here.
 *
 * REFUSED IS NOT A DEAD END: the row falls back to the shell's isolated window
 * where one exists, and otherwise stays the link it always was (SharedWithMe).
 */
export function sharedViewGate(
  a: Parameters<typeof contentFrameGate>[0] & { shell: FrameShell },
): FrameVerdict {
  const framed = contentFrameGate(a)
  if (!framed.ok) return framed
  if (a.shell.kind === 'capacitor' && !a.shell.frameGuard) {
    return { ok: false, why: 'This version of the app cannot show someone else’s artifact inside it yet.' }
  }
  return framed
}

/**
 * WHAT A PAGE FRAMED FROM THE ARTIFACTS' HOST MAY DO — the frame's `sandbox` and
 * `allow`, in one place so the two viewers of them cannot drift (owner, 2026-10-05).
 *
 * `allow-same-origin` is in every set and is not a concession: without it the page
 * loses its cookies (the relay's ticket cookie among them), its storage and its own
 * same-origin requests — the things artifacts exist to publish. It is safe ONLY
 * because the frame's origin is never the App's, which is what the gates above
 * guarantee; these attributes are never used on a frame that did not pass one.
 *
 * NEVER `allow-top-navigation`, in any form. A frame that may navigate the top
 * window can replace the App with a page that looks like it, and inside a shell
 * there is no address bar to give that away.
 *
 * A PAGE THAT IS NOT THIS ACCOUNT'S, INSIDE A SHELL, gets the bare set — no popups,
 * no downloads, no dialogs. Each is a channel into the shell rather than a
 * convenience there: both shells hand a popup's address to the system's opener
 * without looking at its scheme, the Mac shell saves any attachment into Downloads,
 * and Capacitor reads `window.prompt()` from any frame as a command. In a plain
 * browser the same three are an ordinary tab, an ordinary download and an ordinary
 * dialog, so the page keeps them — as does this account's own page, with one
 * exception:
 *
 * NO DIALOGS FOR ANY PAGE INSIDE A CAPACITOR SHELL THAT HAS NOT SAID IT GUARDS ITS
 * BRIDGE — this account's own page included. The owner's ruling covers one's own
 * pages too (2026-10-05: 就算這個頁面, 我是頁面的使用者, 建造者, 我也沒有辦法直接從
 * 頁面來控制我的 agent), and in such a shell `prompt()` is not a dialog but the
 * command channel above. A frame without `allow-modals` cannot raise one at all.
 * A shell that carries `AvengentsFrameGuard/<n>` reads commands from its main frame
 * only (docs/NATIVE.md §16), and there an own page has its dialogs back.
 *
 * `allow` never names the camera, the microphone or screen capture: both shells
 * grant those natively to whichever page is permitted to ask. An own artifact keeps
 * exactly what its frame had before the move; anyone else's gets fullscreen alone.
 */
export function contentFrameAttrs(
  subject: 'own' | 'shared',
  shell: FrameShell,
): { sandbox: string; allow: string } {
  const bare = 'allow-scripts allow-same-origin allow-forms'
  const noDialogs = `${bare} allow-popups allow-popups-to-escape-sandbox allow-downloads`
  const full = `${noDialogs} allow-modals`
  const unguarded = shell.kind === 'capacitor' && !shell.frameGuard
  return {
    sandbox: subject === 'shared' && shell.kind !== 'none' ? bare : unguarded ? noDialogs : full,
    allow: subject === 'own' ? 'clipboard-write; fullscreen' : 'fullscreen',
  }
}

/**
 * What a row's state is, in one word, in the order that matters.
 *
 * The order is deliberate and was paid for on the relay side: a paused relay
 * outranks the terms, which outrank "live", because each earlier one makes the
 * later one not the thing that is wrong.
 */
export function artifactState(
  row: Pick<ArtifactRow, 'live' | 'refused' | 'held'>,
  link: ArtifactLink | null,
  reachable: PairedServer['reachable'],
): { word: string; tone: 'ok' | 'wait' | 'bad'; why: string } {
  if (reachable === 'offline') return { word: 'offline', tone: 'bad', why: 'its machine is not connected' }
  if (link && link.connected && link.paused) {
    return { word: 'paused', tone: 'bad', why: link.paused }
  }
  if (link && link.connected && link.accepted === false) {
    return { word: 'held', tone: 'wait', why: 'the Artifacts terms have not been accepted yet' }
  }
  /*
   * "narrowed", NOT "held" — the terms gate two lines up already owns that
   * word, and the two mean opposite things: held = nothing of yours is served
   * at all; narrowed = it IS being served, just to fewer people than you set.
   * One word apart in the same column is how a reader learns the wrong thing
   * and never finds out.
   */
  if (row.held) return { word: 'narrowed', tone: 'wait', why: row.held }
  if (row.refused) return { word: 'refused', tone: 'bad', why: row.refused }
  if (row.live) return { word: 'live', tone: 'ok', why: 'serving now' }
  if (link && !link.connected) return { word: 'not live', tone: 'wait', why: link.detail || 'the machine is not linked to the relay' }
  return { word: 'not live', tone: 'wait', why: 'the relay has not confirmed it' }
}
